'skip ssr';

import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { useInviteMemberButton } from 'website/src/lib/hooks';
import { getOrgLimitsAndSubs, requireAuth } from 'website/src/lib/ssr';

// import { Button } from 'website/src/components/ui/button'
import { useSession } from '@supabase/auth-helpers-react';
import { TableBlock } from 'beskar/dashboard';
import { Link, useThrowingFn } from 'beskar/landing';
import { prisma } from 'db/prisma';
import { createRepoForSite, deleteSite } from 'website/src/pages/api/functions';
import { removeUserFromOrg } from 'website/src/pages/api/invites';
import { createStripePortal } from 'website/src/pages/api/stripe/stripe-functions';
import { getGithubInstallUrl, groupByFn, slugKebabCase } from 'website/src/lib/utils';
import { MIN_SEATS, WRITE_ACCESS_ROLES, env, prices } from 'website/src/lib/env';
import classNames from 'classnames';
import { OnboardingContainer } from 'website/src/components/OnboardingContainer';
import { useState } from 'react';
import { ModalContent, ModalHeader, ModalBody, ModalFooter, Modal, Button, useDisclosure, Input, Checkbox } from '@nextui-org/react';
import { Slot } from '@radix-ui/react-slot';
import { atoms } from 'website/src/lib/atoms';
export function CreateRepoButton({
  orgId,
  siteId,
  siteName,
  children,
  githubOwner
}) {
  const [name, setName] = useState(slugKebabCase('holocron-' + siteName));
  const [privateRepo, setPrivate] = useState(true);
  const router = useRouter();
  const session = useSession();
  const {
    isOpen,
    onOpen,
    onOpenChange,
    onClose
  } = useDisclosure();
  const {
    fn: create,
    isLoading
  } = useThrowingFn({
    async fn() {
      const {
        branchId
      } = await createRepoForSite({
        repoName: name,
        orgId,
        privateRepo,
        siteId
      });
      onClose();
      await router.replace(router.asPath);
    }
  });
  return <>
            <Slot onClick={() => {
      if (!session) {
        return atoms.showSignupModal.set(true);
      }
      onOpen();
    }}>
                {children}
            </Slot>

            <Modal backdrop='blur' isOpen={isOpen} onOpenChange={onOpenChange}>
                <ModalContent>
                    <ModalHeader className='flex flex-col gap-1'></ModalHeader>
                    <ModalBody className='space-y-3'>
                        <div className='space-y-2'>
                            <Input label='Choose a name for your Github repo' labelPlacement='outside' onValueChange={setName} isRequired aria-label='repo name' placeholder='docs-site' value={name} />
                        </div>

                        <Checkbox className='block' isSelected={privateRepo} onValueChange={setPrivate}>
                            Private Repository
                        </Checkbox>
                    </ModalBody>
                    <ModalFooter>
                        <Button color='danger' variant='light' onPress={onClose}>
                            Cancel
                        </Button>
                        <Button isLoading={isLoading} color='primary' className='font-semibold' onClick={create}>
                            Create repo in {githubOwner}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>;
}
export default function Page({
  users,
  siteId,
  siteName,
  orgId,
  githubOwner,
  githubRepo,
  hasFreeTrial,
  subs,
  installation
}) {
  const router = useRouter();
  const {
    fn: deleteSiteClient,
    isLoading: isDeleting
  } = useThrowingFn({
    async fn() {
      await deleteSite({
        siteId
      });
      await router.push(`/editor`);
    }
  });
  const {
    fn: createPortalClient,
    isLoading: isLoadingPortal
  } = useThrowingFn({
    fn: async () => {
      if (!orgId) {
        window.location.href = `/board`;
        return {
          skipToast: true
        };
      }
      const {
        url
      } = await createStripePortal({
        orgId: orgId,
        callbackPath: window.location.pathname
      });
      window.location.href = url;
      return {
        skipToast: true
      };
    }
  });
  const session = useSession();
  const userId = session?.user?.id;
  // console.log(router.asPath)
  const githubInstallationUrl = getGithubInstallUrl({
    userId,
    orgId,
    siteId,
    redirectToPath: router.asPath,
    flowType: 'update-integration'
  });
  const seats = subs.find(x => [prices.monthlySeatBase.id, prices.yearlySeatBase.id].includes(x.variantId))?.quantity || MIN_SEATS;
  const {
    modal: inviteModal,
    isLoading,
    onInviteClick
  } = useInviteMemberButton({
    hasFreeTrial,
    orgId,
    siteId
  });
  const subGroups = Object.values(groupByFn(subs, x => x.subscriptionId));
  const connectGithubRepo = !installation ? <NextLink legacyBehavior className='contents' href={githubInstallationUrl}>
            <Button aria-label='connect github' className=''>
                Connect Github Account
            </Button>
        </NextLink> : <CreateRepoButton {...{
    orgId,
    siteId,
    siteName,
    githubOwner
  }}>
            <Button>Create Github Repo</Button>
        </CreateRepoButton>;
  return <OnboardingContainer>
            <div className='w-full min-h-[200px] gap-12 flex flex-col rounded'>
                {githubRepo ? <div className='flex flex-col gap-3 '>
                        <div className='flex gap-1 w-full'>
                            <div className=''>Connected to </div>
                            <Link href={`https://github.com/${githubOwner}/${githubRepo}`} className='font-semibold block !truncate'>
                                {githubOwner}/{githubRepo}
                            </Link>
                        </div>
                        <NextLink legacyBehavior className='contents' href={githubInstallationUrl}>
                            <Button className=''>
                                Manage GitHub Integration
                            </Button>
                        </NextLink>
                    </div> : connectGithubRepo}

                <div className='flex flex-col gap-3'>
                    <div className=''>Delete project</div>
                    <Button onClick={async () => {
          const ok = confirm(`Are you sure you want to delete this project? This action is irreversible.`);
          if (!ok) {
            return;
          }
          await deleteSiteClient();
        }} isLoading={isDeleting}
        // bg='red.200'
        // bgDark='red.800'
        >
                        Delete project
                    </Button>
                </div>
                {subGroups.map(subs => {
        const sub = subs[0];
        return <div key={sub.subscriptionId} className='flex flex-col gap-3'>
                            <div className=''>
                                Currently subscribed with {seats} seats
                            </div>
                            <Button onClick={async () => {
            createPortalClient();
          }} isLoading={isLoadingPortal}
          // bg='red.500'
          >
                                Manage subscription
                            </Button>
                        </div>;
      })}
                <div className='flex flex-col gap-3'>
                    <div className='space-y-4'>Members</div>
                    <TableBlock className='border max-w-full' head={['NAME', 'EMAIL', 'ROLE']} footer={<div className='flex'>
                                <div className='text-sm'>
                                    {Math.max(0, seats - users.length)} seats
                                    left
                                </div>
                                <div className='grow'></div>

                                <Button className='text-sm border-0' onClick={onInviteClick} variant='ghost' isLoading={isLoading}>
                                    Invite New User
                                </Button>
                                {inviteModal}
                            </div>} rows={users.map(u => {
          return [<div key='1' className=''>
                                    {u.user.raw_user_meta_data?.['full_name'] || ''}
                                </div>, <div key='2' className=''>
                                    {u.user.email}
                                </div>, <div key='badge' className={classNames('text-xs lowercase font-medium font-mono opacity-80 rounded flex items-center justify-center px-1 py-[2px]', u.role === 'ADMIN' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800')}>
                                    {u.role}
                                </div>, <DeleteUserButton disabled={!users.find(x => x.userId === session?.user?.id && x.role === 'ADMIN') && session?.user?.id !== u.user.id} key='delete' userId={u.userId} />];
        })} />
                </div>
            </div>
        </OnboardingContainer>;
}
export const getServerSideProps = async ctx => {
  const siteId = ctx.params?.siteSlug || '';
  const orgId = ctx.params?.orgId || '';

  // Check if we have a session
  const {
    redirect,
    session,
    supabase
  } = await requireAuth(ctx);
  if (redirect) {
    return {
      redirect
    };
  }
  const userId = session?.user?.id;
  const [{
    subs,
    hasFreeTrial,
    limits
  }, users, repo, installation, site, branches] = await Promise.all([getOrgLimitsAndSubs({
    orgId
  }), prisma.orgsUsers.findMany({
    where: {
      orgId,
      role: {
        in: WRITE_ACCESS_ROLES
      }
    },
    include: {
      user: true
    }
  }), prisma.githubIntegration.findUnique({
    where: {
      siteId: siteId
    }
  }), prisma.githubInstallation.findFirst({
    where: {
      orgId,
      appId: env.GITHUB_APP_ID
    }
  }), prisma.site.findFirst({
    where: {
      siteId
    }
  }), prisma.siteBranch.findMany({
    where: {
      siteId
    }
  })]);
  const githubOwner = repo?.owner || installation?.accountLogin;
  const githubRepo = repo?.repo;
  const siteName = site?.name || '';
  // const siteId = site.siteId
  const props = {
    subs,
    limits,
    siteId,
    users,
    githubOwner,
    githubRepo,
    orgId,
    hasFreeTrial,
    installation,
    siteName,
    branches
  };
  return {
    props
  };
};
function DeleteUserButton({
  userId,
  ...rest
}) {
  const router = useRouter();
  const orgId = router.query.orgId;
  const {
    fn: deleteUserClient,
    isLoading
  } = useThrowingFn({
    async fn() {
      await removeUserFromOrg({
        orgId: orgId,
        userToRemove: userId
      });
      await router.replace(router.asPath);
    }
  });
  return <Button isLoading={isLoading} variant='ghost' className='text-sm text-red-400 border-0' onClick={deleteUserClient} aria-label='delete user' {...rest}>
            Remove
        </Button>;
}